export type QuarterTurn = 0 | 90 | 180 | 270;

export enum RotationSource {
  Sensor = 'sensor',
  DeviceExif = 'device-exif',
  Unknown = 'unknown',
  Flat = 'flat',
  ScreenFollowed = 'screen-followed',
  NotRearCamera = 'not-rear-camera',
}

export enum RotationMethod {
  ExifTag = 'exif-tag',
  CanvasRotate = 'canvas-rotate',
  None = 'none',
  WriteRefused = 'write-refused',
}

export const NO_DEVICE_ORIENTATION = 'none';

export interface OrientationReading {
  readonly rotation: QuarterTurn;
  readonly source: RotationSource;
}

export interface AppliedOrientation {
  readonly captureRotation: QuarterTurn;
  readonly rotationSource: RotationSource;
  readonly method: RotationMethod;
  readonly deviceOrientation?: number | typeof NO_DEVICE_ORIENTATION;
  readonly exifEdit?: string;
  readonly cameraSha256?: string;
}

export interface UpVector {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface PoseState {
  readonly up: UpVector | null;
  readonly turn: QuarterTurn | null;
}

export const FULL_TURN_DEG = 360;
export const QUARTER_TURN_DEG = 90;
export const HALF_QUADRANT_DEG = 45;
export const TURN_HYSTERESIS_DEG = 20;
export const FLAT_IN_PLANE_LIMIT = 0.5;
export const UP_SMOOTHING = 0.3;

export const EMPTY_POSE: PoseState = { up: null, turn: null };
export const UNKNOWN_READING: OrientationReading = { rotation: 0, source: RotationSource.Unknown };
export const NOT_ROTATED: AppliedOrientation = { captureRotation: 0, rotationSource: RotationSource.Unknown, method: RotationMethod.None };

export const EXIF_ORIENTATION_FOR_ROTATION: Readonly<Record<QuarterTurn, number>> = { 0: 1, 90: 6, 180: 3, 270: 8 };
export const ROTATION_FOR_EXIF_ORIENTATION: Readonly<Record<number, QuarterTurn>> = { 1: 0, 2: 0, 3: 180, 4: 180, 5: 90, 6: 90, 7: 270, 8: 270 };

const QUARTER_TURNS: readonly QuarterTurn[] = [0, 90, 180, 270];
const DEG_TO_RAD = Math.PI / 180;

export function toQuarterTurn(degrees: number): QuarterTurn {
  const steps = Math.round(degrees / QUARTER_TURN_DEG);
  return QUARTER_TURNS[((steps % 4) + 4) % 4];
}

export function isQuarterTurn(value: number): value is QuarterTurn {
  return (QUARTER_TURNS as readonly number[]).includes(value);
}

export function upVectorFromTilt(betaDeg: number, gammaDeg: number): UpVector {
  const beta = betaDeg * DEG_TO_RAD;
  const gamma = gammaDeg * DEG_TO_RAD;
  return {
    x: -Math.cos(beta) * Math.sin(gamma),
    y: Math.sin(beta),
    z: Math.cos(beta) * Math.cos(gamma),
  };
}

export function smoothUp(previous: UpVector | null, next: UpVector, factor: number): UpVector {
  if (!previous) {
    return next;
  }
  return {
    x: previous.x + (next.x - previous.x) * factor,
    y: previous.y + (next.y - previous.y) * factor,
    z: previous.z + (next.z - previous.z) * factor,
  };
}

export function inPlaneMagnitude(up: UpVector): number {
  const length = Math.hypot(up.x, up.y, up.z);
  return length > 0 ? Math.hypot(up.x, up.y) / length : 0;
}

export function isFlat(up: UpVector): boolean {
  return inPlaneMagnitude(up) < FLAT_IN_PLANE_LIMIT;
}

export function turnAngle(up: UpVector): number {
  const degrees = Math.atan2(up.x, up.y) / DEG_TO_RAD;
  return (degrees + FULL_TURN_DEG) % FULL_TURN_DEG;
}

export function angularDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % FULL_TURN_DEG;
  return d > FULL_TURN_DEG / 2 ? FULL_TURN_DEG - d : d;
}

export function turnWithHysteresis(angle: number, current: QuarterTurn | null, hysteresis: number): QuarterTurn {
  if (current !== null && angularDistance(angle, current) <= HALF_QUADRANT_DEG + hysteresis) {
    return current;
  }
  return toQuarterTurn(angle);
}

export function nextPose(state: PoseState, sample: UpVector): PoseState {
  const up = smoothUp(state.up, sample, UP_SMOOTHING);
  const turn = isFlat(up) ? state.turn : turnWithHysteresis(turnAngle(up), state.turn, TURN_HYSTERESIS_DEG);
  return { up, turn };
}

export function displayRotation(physicalTurn: QuarterTurn, screenAngle: number): QuarterTurn {
  const unfollowed = toQuarterTurn(physicalTurn - toQuarterTurn(screenAngle));
  return toQuarterTurn(FULL_TURN_DEG - unfollowed);
}

// Only the rotation-lock case is corrected. When the screen has followed the
// phone (angle 90 or 270) the frames are already upright, and correcting there
// would bet a regression on an unproven screen-angle sign.
export function readPose(state: PoseState, screenAngle: number): OrientationReading {
  if (!state.up) {
    return UNKNOWN_READING;
  }
  if (toQuarterTurn(screenAngle) !== 0) {
    return { rotation: 0, source: RotationSource.ScreenFollowed };
  }
  if (isFlat(state.up) || state.turn === null) {
    return { rotation: 0, source: RotationSource.Flat };
  }
  return { rotation: displayRotation(state.turn, screenAngle), source: RotationSource.Sensor };
}

export function appliedFromReading(reading: OrientationReading, method: RotationMethod): AppliedOrientation {
  return {
    captureRotation: reading.rotation,
    rotationSource: reading.source,
    method: reading.rotation === 0 ? RotationMethod.None : method,
  };
}

export interface RotatedCanvasPlan {
  readonly width: number;
  readonly height: number;
  readonly translateX: number;
  readonly translateY: number;
  readonly radians: number;
}

export function rotatedCanvasPlan(sourceWidth: number, sourceHeight: number, rotation: QuarterTurn): RotatedCanvasPlan {
  const sideways = rotation === 90 || rotation === 270;
  const width = sideways ? sourceHeight : sourceWidth;
  const height = sideways ? sourceWidth : sourceHeight;
  return {
    width,
    height,
    translateX: rotation === 90 || rotation === 180 ? width : 0,
    translateY: rotation === 180 || rotation === 270 ? height : 0,
    radians: rotation * DEG_TO_RAD,
  };
}

export interface FrameContext {
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
}

export function drawRotatedFrame(
  canvas: { width: number; height: number },
  context: FrameContext,
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  rotation: QuarterTurn
): void {
  const plan = rotatedCanvasPlan(sourceWidth, sourceHeight, rotation);
  canvas.width = plan.width;
  canvas.height = plan.height;
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.translate(plan.translateX, plan.translateY);
  context.rotate(plan.radians);
  context.drawImage(source, 0, 0, sourceWidth, sourceHeight);
  context.setTransform(1, 0, 0, 1, 0, 0);
}

export function describeCameraFile(applied: AppliedOrientation): string {
  if (applied.deviceOrientation === undefined) {
    return '';
  }
  const device = applied.deviceOrientation === NO_DEVICE_ORIENTATION ? 'none' : String(applied.deviceOrientation);
  const parts = [`camera EXIF orientation ${device}`, `app EXIF edit ${applied.exifEdit ?? 'none'}`];
  if (applied.cameraSha256) {
    parts.push(`camera bytes SHA-256 before the edit ${applied.cameraSha256}`);
  }
  return `Camera file: ${parts.join('; ')}.`;
}

const NOT_ROTATED_REASONS: Readonly<Partial<Record<RotationSource, string>>> = {
  [RotationSource.Flat]: 'Not rotated. The phone was held flat, so its orientation could not be read.',
  [RotationSource.ScreenFollowed]: 'Not rotated. The screen had turned with the phone, so the camera image was already upright.',
  [RotationSource.NotRearCamera]: 'Not rotated. Only the rear camera is corrected, and this camera was not reported as the rear one.',
};

export function describeOrientation(applied: AppliedOrientation): string {
  const turn = `${applied.captureRotation}° clockwise`;
  if (applied.method === RotationMethod.ExifTag) {
    const shown = applied.captureRotation === 0 ? "Displayed as the camera's pixel data lies" : `Displayed rotated ${turn}`;
    return `${shown}. The app set the EXIF orientation flag from the motion sensor at capture, before hashing; the image data is the camera's, unchanged.`;
  }
  if (applied.method === RotationMethod.WriteRefused) {
    return "Not rotated. The motion sensor called for a turn, but the camera's file could not be tagged safely, so it was stored exactly as the camera made it.";
  }
  if (applied.method === RotationMethod.CanvasRotate) {
    return `Live-view frame rotated ${turn} from the motion sensor at capture, before hashing.`;
  }
  if (applied.rotationSource === RotationSource.DeviceExif) {
    return `Not changed by the app. The camera's own EXIF orientation flag displays it rotated ${turn}.`;
  }
  if (applied.rotationSource === RotationSource.Sensor) {
    return applied.captureRotation === 0
      ? 'Not rotated. The motion sensor showed the photo was already upright.'
      : `Not changed by the app. The camera's own EXIF orientation flag (${turn}) already matched the motion sensor.`;
  }
  return NOT_ROTATED_REASONS[applied.rotationSource] ?? 'Not rotated. The phone orientation was not available.';
}
